import React from 'react';
import ChapterHeader from './ChapterHeader';
import ReportCard from '../components/ReportCard';
import { GRADE_DOT, GRADE_TEXT } from '../theme';
import { fmtCount, fmtPct, sentenceCaseMarker, tip } from '../format';

/**
 * The benchmarks table: this company's abnormality prevalence per marker, against the same figure
 * across its industry peers on the platform, against the published India Inc range.
 *
 * Every judgement here is the backend's. The grade (below the range / inside it / above it) arrives
 * next to each rate so the colour and the number can never disagree, the industry column arrives
 * already withheld when the peer set is too small to be a benchmark, and the India Inc ranges are
 * served with the payload — nothing on this card is typed into the frontend.
 */

/**
 * Whether there is a table to draw at all — a contract whose entitlement covers none of these markers
 * comes back with no rows. Exported because the page needs the same answer to decide the gap this
 * section leaves behind when it renders nothing, and two copies of the rule would drift apart.
 */
export const isBenchmarksEmpty = (data) => !((data && data.rows) || []).length;

const GRADE_CAPTION = {
  good: 'below the India Inc range',
  watch: 'within the India Inc range',
  risk: 'above the India Inc range',
};

/** A graded rate cell: the mono figure in its verdict colour, or an em-dash when unmeasured. */
const RateCell = ({ cell, tooltip }) => {
  if (!cell || cell.rate === null || cell.rate === undefined) {
    return (
      <td className='px-ds-3 py-[13px] text-center font-jetbrains-mono text-[14px] text-gray-400' data-tip={tooltip}>
        –
      </td>
    );
  }

  return (
    <td
      className={`px-ds-3 py-[13px] text-center font-jetbrains-mono text-[14px] font-semibold tabular-nums ${
        GRADE_TEXT[cell.grade] || 'text-gray-900'
      }`}
      data-tip={tooltip}
    >
      {fmtPct(cell.rate)}
    </td>
  );
};

const companyTip = (row) =>
  row.company && row.company.rate !== null
    ? tip(
        `${row.label}`,
        `${fmtCount(row.company.affected)} of ${fmtCount(row.company.tested)} tested were abnormal`,
        row.company.grade ? GRADE_CAPTION[row.company.grade] : null
      )
    : tip(row.label, 'Not measured in this period');

/* The backend walks its peer-set ladder per marker (same industry → related industries → the whole
   platform) and each cell carries the tier that answered it, so the tooltip names its actual source. */
const industryTip = (row) => {
  if (!row.industry || row.industry.rate === null) {
    return tip('No peer set on the platform has enough screened employees for this marker');
  }
  return tip(
    `${row.industry.source_label} · ${fmtCount(row.industry.peer_companies)} companies`,
    `${fmtCount(row.industry.affected)} of ${fmtCount(row.industry.tested)} tested were abnormal`
  );
};

/**
 * The insights line is derived, not asserted: the two markers with the widest lead over the industry
 * (when an industry column exists), and every marker graded above the India Inc range.
 */
const insights = (rows, orgName) => {
  const outperforms = rows
    .filter(
      (row) =>
        row.industry
        && row.industry.rate !== null
        && row.company.rate !== null
        && row.company.rate < row.industry.rate
    )
    .map((row) => ({ label: row.label, gap: row.industry.rate - row.company.rate }))
    .sort((a, b) => b.gap - a.gap)
    .slice(0, 2);

  const priorities = rows.filter((row) => row.company.grade === 'risk');

  const parts = [];
  if (outperforms.length) {
    parts.push(
      `${orgName || 'This company'} outperforms its industry on `
        + outperforms.map((entry) => `${sentenceCaseMarker(entry.label)} (−${entry.gap.toFixed(0)}pp)`).join(' and ')
        + '.'
    );
  }
  if (priorities.length) {
    parts.push(
      priorities
        .map((row) => `${sentenceCaseMarker(row.label)} (${fmtPct(row.company.rate)})`)
        .join(', ')
        + ` ${priorities.length === 1 ? 'remains a priority intervention area' : 'remain priority intervention areas'}.`
    );
  }
  return parts;
};

const Benchmarks = ({ data, orgName }) => {
  const rows = data.rows || [];
  const industry = data.industry || {};
  const keyInsights = insights(rows, orgName);
  // Which peer sets actually answered, in row order — drives the footnote so a reader can tell an
  // exact-industry figure from a platform-wide fallback without hovering every cell.
  const sourceLabels = [
    ...new Set(rows.filter((row) => row.industry).map((row) => row.industry.source_label)),
  ];

  if (isBenchmarksEmpty(data)) return null;

  // Opens the report, so it reads as a chapter in its own right rather than a card appended to one: the
  // same ChapterHeader the two chapters use, and the same gap under the chapter nav that chapter 01 used
  // to take. The wider 80px gap now separates the chapters that follow it. The `#benchmarks` anchor is
  // on the page's wrapper rather than here, so the nav's first pill has a target in every state — this
  // section renders nothing without rows, and its skeleton and error state are the page's to draw.
  return (
    <section className='mt-ds-9'>
      <ChapterHeader
        title={`Employee health metrics: ${orgName || 'this company'} vs. industry benchmarks`}
        subtitle={
          industry.name && industry.sufficient
            ? `Abnormality prevalence per marker, against ${industry.name} peers on ekincare and published India Inc ranges`
            : 'Abnormality prevalence per marker, against peer companies on ekincare and published India Inc ranges'
        }
      />

      <ReportCard fileName='industry-benchmarks' className='mt-ds-8 p-[26px]'>
        <div className='overflow-x-auto'>
          <table className='w-full border-collapse'>
            <thead>
              <tr>
                <th className='px-ds-3 py-[11px] text-left text-[13px] font-semibold text-gray-900'>
                  Parameters
                </th>
                <th className='px-ds-3 py-[11px] text-center text-[13px] font-semibold text-gray-900'>
                  {`${orgName || 'Company'} results`}
                </th>
                <th className='px-ds-3 py-[11px] text-center text-[13px] font-semibold text-gray-900'>
                  Similar industry
                </th>
                <th className='px-ds-3 py-[11px] text-center text-[13px] font-semibold text-gray-900'>
                  India Inc ranges
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className='border-t border-dotted border-gray-400'>
                  <td className='px-ds-3 py-[13px] text-sm font-medium text-gray-900'>
                    {row.label}
                    {row.note ? (
                      <small className='mt-0.5 block text-[11px] font-normal text-gray-600'>{row.note}</small>
                    ) : null}
                  </td>
                  <RateCell cell={row.company} tooltip={companyTip(row)} />
                  <RateCell cell={row.industry} tooltip={industryTip(row)} />
                  <td
                    className='px-ds-3 py-[13px] text-center font-jetbrains-mono text-[14px] tabular-nums text-gray-800'
                    data-tip={tip(`${row.label}`, 'Published India Inc prevalence range')}
                  >
                    {`${row.benchmark.low}-${row.benchmark.high}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className='mt-ds-4 flex flex-wrap items-center gap-ds-5 border-t border-gray-300 pt-ds-4'>
          {Object.entries(GRADE_CAPTION).map(([grade, caption]) => (
            <div key={grade} className='flex items-center gap-[7px] text-[12px] text-gray-700'>
              <span className={`h-2.5 w-2.5 rounded-full ${GRADE_DOT[grade]}`} />
              {caption[0].toUpperCase() + caption.slice(1)}
            </div>
          ))}
          {sourceLabels.length ? (
            <div className='text-[12px] text-gray-600'>
              {`Similar-industry figures come from ${sourceLabels.join(', then ')}; each cell's tooltip names its peer set.`}
            </div>
          ) : null}
          {!industry.sufficient ? (
            <div className='text-[12px] text-gray-600'>
              {`Industry comparison needs at least ${fmtCount(industry.min_peer_companies)} peer companies on the platform.`}
            </div>
          ) : null}
        </div>

        {keyInsights.length ? (
          <div className='mt-ds-4 rounded-ds-lg bg-gray-100 px-ds-4 py-ds-3 text-[13px] leading-[1.6] text-gray-800'>
            <strong className='font-semibold text-gray-900'>Key insights: </strong>
            {keyInsights.join(' ')}
          </div>
        ) : null}
      </ReportCard>
    </section>
  );
};

export default Benchmarks;
